import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Breadcrumbs,
  CircularProgress,
  FormControl,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { Link as RouterLink, useHistory, useLocation, useParams } from 'react-router-dom'
import { getDefaultOverlay, getSampleValues } from '../../Apis/defaultOverlays'
import OverlayEditor from '../CmsAdminPage/BrandingTab/OverlayEditor'
import { labelForSlug, variantLabelForSlug } from '../../utils/constants/overlayTypes'
import { buildInitialOverlay } from './initialOverlay'
import { fieldsFromSampleValues } from './overlayFields'

// Heuristic: derive FieldDefinition-shaped objects from the {key: sampleValue} dict the
// /sample-values endpoint returns. The default-overlays API doesn't expose a /fields route
// today (only season-scoped overlays do), so we reconstruct what the editor needs from the
// sample-values keys + values. Sample paths starting with "/" are images (the registry only
// uses /default-cards/... paths for image samples); everything else is text. Labels are
// derived by title-casing the snake_case key.
export default function DefaultOverlayEditorPage() {
  const { slug } = useParams()
  const location = useLocation()
  const history = useHistory()

  // ?variant=N → number | null. NaN guard so ?variant= (empty) doesn't poison the API call.
  const variant = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('variant')
    if (raw === null || raw === '') return null
    const n = parseInt(raw, 10)
    return Number.isFinite(n) ? n : null
  }, [location.search])

  // ?sport=Volleyball. Only the scoreboard has a second board; anything else the backend
  // 404s via OverlaySportSupport rather than quietly serving the basketball artwork.
  // urlSport keeps the URL clean (basketball needs no query param); sport is what the API
  // gets, and the API requires it — the backend no longer assumes a sport on the caller's
  // behalf, so the page has to say which board it is editing.
  const urlSport = useMemo(
    () => new URLSearchParams(location.search).get('sport') || null,
    [location.search],
  )
  const sport = urlSport ?? 'Basketball'

  // ?statsMode=LiteV2. Only Team Comparison ships a second board; every other type 404s under
  // a LiteV2 URL. Full carries no query param, so urlStatsMode is null there and statsMode —
  // what the API gets — is 'Full'.
  const urlStatsMode = useMemo(
    () => new URLSearchParams(location.search).get('statsMode') || null,
    [location.search],
  )
  const statsMode = urlStatsMode ?? 'Full'

  const [data, setData] = useState(null)
  const [sampleValues, setSampleValues] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    Promise.resolve()
      .then(() => getDefaultOverlay(slug, variant, sport, statsMode))
      .then((overlay) =>
        // Fetch sample values for the resolved targetMode the backend chose for us — the
        // overlay response carries the canonical mode (single/both) so we don't have to
        // re-derive it client-side.
        getSampleValues(slug, overlay?.targetMode, sport, statsMode).then((samples) => ({
          overlay,
          samples,
        })),
      )
      .then(({ overlay, samples }) => {
        if (cancelled) return
        setData(overlay)
        setSampleValues(samples)
      })
      .catch((err) => {
        if (cancelled) return
        const status = err?.response?.status
        if (status === 404) {
          setError(
            'Default-overlay editor is unavailable. The backend must be ' +
              'running in Development with DefaultOverlays:EditorEnabled.',
          )
        } else {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              'Failed to load default overlay',
          )
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug, variant, sport, statsMode])

  const handleVariantChange = (e) => {
    const next = e.target.value
    // Use replace (not push) so the back button still returns to the list page rather
    // than cycling through every variant the user previewed.
    const q = new URLSearchParams({
      variant: next,
      ...(urlSport ? { sport: urlSport } : {}),
      ...(urlStatsMode ? { statsMode: urlStatsMode } : {}),
    })
    history.replace(`/default-overlays/${slug}?${q}`)
  }

  const handleSportChange = (e) => {
    const next = e.target.value
    // Variant is deliberately dropped: it is a per-sport dimension and the volleyball
    // board has none, so the backend re-resolves the default for whichever board loads.
    history.replace(`/default-overlays/${slug}?sport=${next}`)
  }

  const handleStatsModeChange = (e) => {
    const next = e.target.value
    // Variant is dropped as on a sport switch — each board resolves its own default.
    history.replace(
      next === 'Full' ? `/default-overlays/${slug}` : `/default-overlays/${slug}?statsMode=${next}`,
    )
  }

  // Re-create the initialOverlay reference whenever data changes so OverlayEditor remounts
  // and reseeds its internal state. The editor reads `initialOverlay` only on first render,
  // so without the key change a variant switch wouldn't actually swap the canvas content.
  const editorKey = useMemo(() => {
    if (!data) return null
    // resolvedVariant is null for non-variant types; coerce to '' so the key still changes
    // between e.g. Starters single→both reloads.
    return `${slug}|${data.variant ?? ''}|${sport ?? ''}|${statsMode}`
  }, [slug, data, sport, statsMode])

  const handleEditorSettled = useCallback(() => {
    // Send the user back to the list page on cancel — same UX as BrandingTab's editor.onCancel.
    history.push('/default-overlays')
  }, [history])

  const label = labelForSlug(slug)
  const availableVariants = data?.availableVariants ?? []
  const availableSports = data?.availableSports ?? []
  const availableStatsModes = data?.availableStatsModes ?? []
  const fields = useMemo(() => fieldsFromSampleValues(sampleValues), [sampleValues])

  const initialOverlay = useMemo(() => buildInitialOverlay(data), [data])

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumbs aria-label='breadcrumb' style={{ marginBottom: 8 }}>
        <MuiLink component={RouterLink} color='inherit' to='/default-overlays'>
          Default overlays
        </MuiLink>
        <Typography color='textPrimary'>{label}</Typography>
      </Breadcrumbs>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        <Typography variant='h5' style={{ flex: 1 }}>
          Edit default: {label}
        </Typography>
        {availableSports.length > 1 && (
          <FormControl variant='outlined' size='small' style={{ minWidth: 140 }}>
            <InputLabel id='sport-select-label'>Sport</InputLabel>
            <Select
              labelId='sport-select-label'
              label='Sport'
              value={sport ?? availableSports[0]}
              onChange={handleSportChange}
            >
              {availableSports.map((sp) => (
                <MenuItem key={sp} value={sp}>{sp}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* LiteV2 is shown as "Lite" — what the product calls it; v1 Lite is unassignable. */}
        {availableStatsModes.length > 1 && (
          <FormControl variant='outlined' size='small' style={{ minWidth: 140 }}>
            <InputLabel id='stats-mode-select-label'>Stats mode</InputLabel>
            <Select
              labelId='stats-mode-select-label'
              label='Stats mode'
              value={statsMode}
              onChange={handleStatsModeChange}
            >
              {availableStatsModes.map((sm) => (
                <MenuItem key={sm} value={sm}>{sm === 'LiteV2' ? 'Lite' : sm}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {availableVariants.length > 0 && (
          <FormControl variant='outlined' size='small' style={{ minWidth: 120 }}>
            <InputLabel id='variant-select-label'>Variant</InputLabel>
            <Select
              labelId='variant-select-label'
              label='Variant'
              value={data?.variant ?? availableVariants[0]}
              onChange={handleVariantChange}
            >
              {availableVariants.map((v) => (
                <MenuItem key={v} value={v}>{variantLabelForSlug(slug, v)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <CircularProgress />
        </div>
      )}

      {!loading && error && (
        <Alert severity='error' style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && data && initialOverlay && (
        <OverlayEditor
          key={editorKey}
          mode='default'
          type={slug}
          variant={data.variant ?? null}
          sport={sport}
          statsMode={statsMode}
          fields={fields}
          initialOverlay={initialOverlay}
          // Default overlays don't carry the season-mode target-mode picker — the
          // bundled JSON's TargetMode is the canonical setting and the editor hides
          // the toggle in default mode. Keep both off so the editor takes the
          // initialOverlay.targetMode untouched.
          supportsTargetMode={false}
          defaultTargetMode={data.targetMode ?? 'Single'}
          onCancel={handleEditorSettled}
        />
      )}
    </div>
  )
}
